// Unpublished Work © 2020-2025 Deere & Company.

import t from 'tcomb';
import {DomainData} from './domain-data';
import {ApplicationStatus} from './application-status';
import {ValidationError} from './validation-error';
import {customerTypes} from '../../enum/customer-type';
import {countriesWithTaxIds} from '../../../literals/country-codes';
import {Customer} from '../customer';
import {OriginalCustomerValues} from './original-customer-values';
import {CriticalCustomerNameValues} from '../../../literals/critical-customer-name-values';
import {validateHardRequired, validateSoftRequired} from './validation-helpers';
import {DataShare} from './data-share';
import {isFeatureToggleEnableForJDFMobilePhone} from '../../../services/jdf-services/check-jdf-name-details-read-only';

export const AddCustomerDetail = t.struct({
    customer: Customer,
    applicationStatus: ApplicationStatus,
    dataShare: DataShare,
    domainData: DomainData,
    potentialMatches: t.list(Customer, 'PotentialMatches'),
    validationError: ValidationError,
    originalCustomerValues: OriginalCustomerValues
}, {
    defaultProps: {
        customer: new Customer({}),
        dataShare: new DataShare({}),
        applicationStatus: new ApplicationStatus({}),
        domainData: new DomainData({}),
        potentialMatches: [],
        validationError: new Set(),
        originalCustomerValues: new OriginalCustomerValues({})
    },
    name: 'AddCustomerDetail',
    strict: true
});

const checkIfAllEnabledAndRequiredFieldsAreFilledForAddress = (address) => {
    const {addressElements} = address.addressComponent;
    const listOfRequireElements = Object.keys(addressElements).reduce((requireElement, elementKey) => {
        if (addressElements[elementKey].enabled && addressElements[elementKey].required) {
            requireElement.push(elementKey);
        }

        return requireElement;
    }, []);

    return listOfRequireElements.length === 0 || listOfRequireElements.every((field) => address[field]);
};

const checkPhoneIsValidForSave = (phoneNumber) => [
    phoneNumber.areaCode,
    phoneNumber.phoneNumber,
    phoneNumber.formattedPhone,
    phoneNumber.countryCode
].every((condition) => condition);

const checkAddressState = ({communication}) => {
    const {
        mailingAddress,
        physicalAddress
    } = communication;
    const isMailingAddressInteracted = mailingAddress.canAddressSectionBeReset();
    const isPhysicalAddressInteracted = physicalAddress.canAddressSectionBeReset();
    const isMailingAddressComplete = checkIfAllEnabledAndRequiredFieldsAreFilledForAddress(communication.mailingAddress);
    const isPhysicalAddressComplete = checkIfAllEnabledAndRequiredFieldsAreFilledForAddress(communication.physicalAddress);
    const bothAddressTypesFilled = (addressOneStatus, addressTwoStatus) => addressOneStatus && addressTwoStatus;
    const onlyOneAddressFilled = (addressOneStatus, addressTwoStatus) => addressOneStatus && !addressTwoStatus;

    return [
        bothAddressTypesFilled(isPhysicalAddressComplete, isMailingAddressComplete),
        onlyOneAddressFilled(isPhysicalAddressComplete, isMailingAddressInteracted),
        onlyOneAddressFilled(isMailingAddressComplete, isPhysicalAddressInteracted)
    ].some((condition) => condition);
};

const checkJDFAddressState = ({communication}) => {
    const {
        mailingAddress,
        physicalAddress
    } = communication;
    const isMailingAddressComplete = checkIfAllEnabledAndRequiredFieldsAreFilledForAddress(mailingAddress);
    const isPhysicalAddressComplete = checkIfAllEnabledAndRequiredFieldsAreFilledForAddress(physicalAddress);
    const bothAddressTypesFilled = (addressOneStatus, addressTwoStatus) => addressOneStatus && addressTwoStatus;

    return [
        bothAddressTypesFilled(isPhysicalAddressComplete, isMailingAddressComplete)
    ].every((condition) => condition);
};

export const checkJDFCustomerDetailsRequireFields = (countryCode) => {
    const usFields = {
        [customerTypes.INDIVIDUAL]: (customer) => [
            customer.customerName.firstName,
            customer.customerName.lastName,
            customer.birthDate,
            customer.isMiddleNameValidForSave(),
            customer.ssn,
            customer.usCtznInd !== undefined && customer.usCtznInd !== null
        ].every((condition) => condition),
        [customerTypes.BUSINESS]: (customer) => [
            customer.customerName.businessName1,
            customer.customerName.businessType !== '' && customer.customerName.businessType !== '0',
            customer.taxId,
            customer.filingDetails.filingStateCode !== '' && customer.filingDetails.filingStateCode !== '0'
        ].every((condition) => condition),
        [customerTypes.BUSINESS_CONTACT]: (customer) => [
            customer.customerName.businessName1,
            customer.customerName.businessType !== '' && customer.customerName.businessType !== '0',
            customer.taxId,
            customer.filingDetails.filingStateCode !== '' && customer.filingDetails.filingStateCode !== '0'
        ].every((condition) => condition)
    };

    const caFields = {
        [customerTypes.INDIVIDUAL]: (customer) => [
            customer.customerName.firstName,
            customer.customerName.lastName,
            customer.birthDate,
            customer.isMiddleNameValidForSave()
        ].every((condition) => condition),
        [customerTypes.BUSINESS]: (customer) => [
            customer.customerName.businessName1,
            customer.customerName.businessType !== '' && customer.customerName.businessType !== '0',
            customer.filingDetails.filingStateCode !== '' && customer.filingDetails.filingStateCode !== '0'
        ].every((condition) => condition),
        [customerTypes.BUSINESS_CONTACT]: (customer) => [
            customer.customerName.businessName1,
            customer.customerName.businessType !== '' && customer.customerName.businessType !== '0',
            customer.filingDetails.filingStateCode !== '' && customer.filingDetails.filingStateCode !== '0'
        ].every((condition) => condition)
    };

    return countryCode === 'US' ? usFields : caFields;
};

export const checkNonJDFHasMembershipCustomerDetailsRequireFields = () => ({
    [customerTypes.INDIVIDUAL]: (customer) => [
        customer.customerName.firstName,
        customer.customerName.lastName,
        customer.isMiddleNameValidForSave()
    ].every((condition) => condition),
    [customerTypes.BUSINESS]: (customer) => [
        customer.customerName.businessName1
    ].every((condition) => condition),
    [customerTypes.BUSINESS_CONTACT]: (customer) => [
        customer.customerName.businessName1
    ].every((condition) => condition)
});

const checkJDFPhoneNumber = ({communication}) => {
    const {
        homePhone,
        workPhone,
        mobilePhone
    } = communication;

    return [
        checkPhoneIsValidForSave(homePhone),
        checkPhoneIsValidForSave(workPhone),
        checkPhoneIsValidForSave(mobilePhone)
    ].some((condition) => condition);
};

const checkPrivacyIsARequirementOrNot = (showPrivacyFooter, providedPrivacyPolicy) =>
    showPrivacyFooter ? providedPrivacyPolicy : true;

const checkCustomerDetailsRequireFields = () => ({
    [customerTypes.INDIVIDUAL]: ({customerName}) => [
        customerName.firstName,
        customerName.lastName
    ].every((condition) => condition),
    [customerTypes.BUSINESS]: ({customerName}) => [
        customerName.businessName1
    ].every((condition) => condition),
    [customerTypes.BUSINESS_CONTACT]: ({customerName}) => [
        customerName.firstName,
        customerName.lastName
    ].every((condition) => condition)
});

const checkThereAreNoValidationError = (validationError) => validationError === 0;

AddCustomerDetail.prototype.getTaxIdOptions = function (taxCode) {
    if (taxCode) {
        return [{
            label: taxCode,
            value: taxCode
        }];
    }
    return [];
};

const showTaxIDBasedOnCountry = (country) => Object.values(countriesWithTaxIds).includes(country);

const hasValidMobile = (mobile) => mobile && mobile.areaCode && mobile.phoneNumber;
const isMobileCheckInvalid = (mobile) => mobile && mobile.mobilePhoneCheck;

const checkJDFMobilePhoneNumber = ({communication}) => {
    const mobile = communication.mobilePhone;

    if (hasValidMobile(mobile)) {
        return true;
    }
    return !isMobileCheckInvalid(mobile);
};

AddCustomerDetail.prototype.showTaxIDField = function () {
    return showTaxIDBasedOnCountry(this.applicationStatus.addressCountryCode);
};

AddCustomerDetail.prototype.areHardRequirementMet = function (selectedInfo) {
    const {
        customerType,
        physicalCountry,
        mailingCountry
    } = selectedInfo;

    const combinedHardRequirements = this.domainData.getHardRequiredFields(physicalCountry, mailingCountry, customerType);

    if (combinedHardRequirements.length > 0) {
        return validateHardRequired(combinedHardRequirements, this.customer.governmentTaxIds);
    }
    return true;
};

AddCustomerDetail.prototype.areSoftRequirementsMet = function (selectedInfo) {
    const {
        customerType,
        physicalCountry,
        mailingCountry
    } = selectedInfo;

    const combinedSoftRequirements = this.domainData.getSoftRequiredFields(physicalCountry, mailingCountry, customerType);

    if (combinedSoftRequirements.length > 0) {
        return validateSoftRequired(combinedSoftRequirements, this.customer.governmentTaxIds);
    }
    return true;
};

AddCustomerDetail.prototype.isTaxIdValidForSave = function () {
    const selectedInfo = {
        customerType: this.customer.customerType,
        physicalCountry: this.customer.communication.physicalAddress.countryCode,
        mailingCountry: this.customer.communication.mailingAddress.countryCode
    };

    let hardRequirement,
        softRequirement;

    if (selectedInfo.customerType === customerTypes.BUSINESS_CONTACT) {
        return true;
    }

    if (this.domainData.allTaxIds.length > 0) {
        hardRequirement = this.areHardRequirementMet(selectedInfo);
        softRequirement = this.areSoftRequirementsMet(selectedInfo);
    }

    return [
        hardRequirement,
        softRequirement
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isSaveButtonEnabled = function () {
    return [
        checkThereAreNoValidationError(this.validationError.size),
        checkCustomerDetailsRequireFields()[this.customer.customerType](this.customer),
        checkAddressState(this.customer),
        checkPrivacyIsARequirementOrNot(this.customer.showPrivacyFooter(this.domainData.getUniquePrivacyPolicyCountries()).result, this.customer.providedPrivacyPolicy),
        this.customer.isEmailValidForSave(),
        this.isTaxIdValidForSave()
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isBusinessContactSaveEnabled = function () {
    return [
        checkThereAreNoValidationError(this.validationError.size),
        checkCustomerDetailsRequireFields()[this.customer.customerType](this.customer),
        checkPrivacyIsARequirementOrNot(this.customer.showPrivacyFooter(this.domainData.getUniquePrivacyPolicyCountries()).result, this.customer.providedPrivacyPolicy),
        this.customer.isEmailValidForSave()
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isEditSaveButtonEnabled = function () {
    return [
        checkThereAreNoValidationError(this.validationError.size),
        checkCustomerDetailsRequireFields()[this.customer.customerType](this.customer),
        checkAddressState(this.customer),
        this.customer.isEmailValidForSave(),
        this.isTaxIdValidForSave()
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isConfirmationRequired = function () {
    return !CriticalCustomerNameValues.every(
        (valueToConfirm) => this.customer.customerName[valueToConfirm] === this.originalCustomerValues[valueToConfirm]
    );
};

AddCustomerDetail.prototype.areCountriesEqual = function (addressType) {
    const registryCountryCode = this.customer.communication[addressType].countryCode || this.domainData.selectedValues.country;
    const compareCountryCode = this.dataShare.customer.communication[addressType].countryCode || this.domainData.selectedValues.country;

    return registryCountryCode === compareCountryCode;
};

AddCustomerDetail.prototype.isRegistrySaveButtonEnabled = function () {
    return [
        checkThereAreNoValidationError(this.validationError.size),
        checkCustomerDetailsRequireFields()[this.customer.customerType](this.customer),
        checkAddressState(this.customer),
        this.customer.isEmailValidForSave()
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isJDFSaveButtonEnabled = function (countryCode) {
    return [
        checkThereAreNoValidationError(this.validationError.size),
        checkJDFCustomerDetailsRequireFields(countryCode)[this.customer.customerType](this.customer),
        checkJDFPhoneNumber(this.customer),
        isFeatureToggleEnableForJDFMobilePhone() && checkJDFMobilePhoneNumber(this.customer),
        checkJDFAddressState(this.customer),
        this.customer.isEmailValidForSave()
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isNonJDFWithMembershipEnabled = function () {
    return [
        checkThereAreNoValidationError(this.validationError.size),
        checkNonJDFHasMembershipCustomerDetailsRequireFields()[this.customer.customerType](this.customer),
        checkJDFPhoneNumber(this.customer),
        isFeatureToggleEnableForJDFMobilePhone() && checkJDFMobilePhoneNumber(this.customer),
        checkJDFAddressState(this.customer),
        this.customer.isEmailValidForSave()
    ].every((condition) => condition);
};

AddCustomerDetail.prototype.isPhysicalAddressFilled = function () {
    const physicalAddress = this.customer.communication.physicalAddress;

    return checkIfAllEnabledAndRequiredFieldsAreFilledForAddress(physicalAddress);
};
